import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

import { FHSelect } from '../../../../../../../../base';
import ErrorBox from '../../../errorBox';
import NumericInput from '../../../numericInput';
import { goalActions } from '../../../../../../../../../store';
import { validatePCGMasteryCriteria } from '../../../../../common/utils/general';
import { FORTA_ERROR, FORTA_GRAY2 } from '../../../../../../../../../utils/colors';
import { BackgroundTypes } from '../../../../../../../../../constants';
import { FREQUENCY_OPTIONS } from '../common/constants/general';

import type { TreatmentPlanResourceVersion } from '../../../../constants/general';
import type {
  IPCGMasteryCriteria,
  ITreatmentPlanGoal,
  SelectValueType,
} from '../../../../../../../../../utils/types';

interface ISAGPercentageMasteryCriteriaState {
  total_attempts: number | null;
  desired_amount: number | null;
  practice_duration_count: number | null;
}

interface IPercentageMasteryCriteria {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
  goalVersion: TreatmentPlanResourceVersion;
}

interface IPercentageMCRowProps {
  isEditing: boolean;
  goalIndex: number;
  values: ISAGPercentageMasteryCriteriaState;
  showFrequency?: boolean;
  maintenance_frequency?: string | null;
  isEditable: boolean;
  goalVersion: TreatmentPlanResourceVersion;
  updateMaintenanceFrequency?: (value: string) => void;
  onUpdate: (key: string, value: number | null) => void;
}

function isPCGPercentageMasteryCriteria(
  value: ITreatmentPlanGoal['mastery_criteria'],
): value is IPCGMasteryCriteria {
  return value !== null && 'desired_amount' in value && !('acquisition' in value);
}

function PercentageMCRow({
  isEditing,
  isEditable,
  goalIndex,
  values,
  goalVersion,
  showFrequency = false,
  maintenance_frequency = null,
  updateMaintenanceFrequency,
  onUpdate,
}: IPercentageMCRowProps) {
  const errors = validatePCGMasteryCriteria(values, values.total_attempts, goalVersion);
  const hasErrors = Object.keys(errors).length > 0;
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: `1px solid ${hasErrors ? FORTA_ERROR : FORTA_GRAY2}`,
        borderRadius: '12px',
      }}
    >
      {hasErrors && (
        <Typography textAlign="left" color={FORTA_ERROR} mt={2} mb={1}>
          {errors.desired_amount || errors.practice_duration_count}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        {isEditing ? (
          <NumericInput
            testId={`pcgMCDesiredLevel-${goalIndex}`}
            disabled={!isEditable}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
            }}
            value={values.desired_amount}
            onChange={(value) => {
              onUpdate('desired_amount', value ? parseInt(value) : null);
            }}
            error={!values.desired_amount}
          />
        ) : values.desired_amount ? (
          <Typography textAlign="left" display="inline" fontWeight={700} sx={{ mx: 1 }}>
            {values.desired_amount}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
          {`out of `}
        </Typography>
        {isEditing ? (
          <NumericInput
            testId={`masterCriteriaPracticeTotalAttempts-${goalIndex}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
            }}
            disabled={!isEditable}
            value={values.total_attempts}
            onChange={(value) => {
              onUpdate('total_attempts', value ? parseInt(value) : null);
            }}
            error={!values.total_attempts}
          />
        ) : values.total_attempts ? (
          <Typography textAlign="left" display="inline" fontWeight={700} sx={{ mx: 1 }}>
            {values.total_attempts}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
          {` opportunities observed by BCBA, or self reported by parent during Parent Training over `}
        </Typography>
        {isEditing ? (
          <NumericInput
            testId={`masterCriteriaPracticeDurationCount-${goalIndex}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
            }}
            disabled={!isEditable}
            value={values.practice_duration_count}
            onChange={(value) => {
              onUpdate('practice_duration_count', value ? parseInt(value) : null);
            }}
            error={!values.practice_duration_count}
          />
        ) : values.practice_duration_count ? (
          <Typography textAlign="left" display="inline" fontWeight={700} sx={{ mx: 1 }}>
            {values.practice_duration_count}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: 0.5 }}>
          consecutive meetings.
        </Typography>
      </Box>
      {showFrequency && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            my: 1,
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Frequency
          </Typography>
          {isEditing ? (
            <FHSelect
              data-testid={`maintenanceFrequencyDropDown-${goalIndex}`}
              sx={{ minWidth: 200 }}
              inputHeight={40}
              backgroundType={BackgroundTypes.Colored}
              displayEmpty
              disabled={!isEditable}
              renderValue={
                maintenance_frequency === null
                  ? () => <Typography>Select frequency</Typography>
                  : undefined
              }
              options={FREQUENCY_OPTIONS}
              optionDataTestIdPrefix="maintenanceFrequencyOption"
              value={maintenance_frequency || ''}
              onUpdate={(value: SelectValueType) => {
                const selectedFrequency = FREQUENCY_OPTIONS.find(
                  (option) => option.value === value,
                )?.value;
                if (updateMaintenanceFrequency && selectedFrequency) {
                  updateMaintenanceFrequency(selectedFrequency);
                }
              }}
              error={!maintenance_frequency}
            />
          ) : (
            <Typography align="left">
              {FREQUENCY_OPTIONS.find((option) => option.value === maintenance_frequency)?.label ||
                ''}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function PCGPercentageMasteryCriteriaV1({
  goal,
  goalIndex,
  isEditable,
  goalVersion,
}: IPercentageMasteryCriteria) {
  const dispatch = useDispatch();

  const { mastery_criteria } = goal;
  const percentageMasteryCriteria = isPCGPercentageMasteryCriteria(mastery_criteria)
    ? mastery_criteria
    : null;
  const { desired_amount, practice_duration_count, total_attempts } =
    percentageMasteryCriteria ?? {};

  const [masteryCriteriaState, setMasteryCriteriaMCState] =
    useState<ISAGPercentageMasteryCriteriaState>({
      total_attempts: total_attempts ?? null,
      desired_amount: desired_amount ?? null,
      practice_duration_count: practice_duration_count ?? null,
    });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const { mastery_criteria } = goal;
    const percentageMasteryCriteria = isPCGPercentageMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { desired_amount, practice_duration_count, total_attempts } =
      percentageMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasteryCriteriaMCState({
      total_attempts: total_attempts ?? null,
      desired_amount: desired_amount ?? null,
      practice_duration_count: practice_duration_count ?? null,
    });
  }, [goal?.is_editing, goal?.id]);

  const handleMCState = (key: string, value: number | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: masteryCriteriaState,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  return (
    <>
      <PercentageMCRow
        onUpdate={handleMCState}
        goalIndex={goalIndex}
        isEditing={Boolean(goal?.is_editing)}
        values={masteryCriteriaState}
        isEditable={isEditable}
        goalVersion={goalVersion}
      />
    </>
  );
}

export default PCGPercentageMasteryCriteriaV1;
